<script setup lang="ts">
	type Props = {
		currentPage: number
		totalItems: number
		perPage: number
		pageRange?: number
	}
	const props = withDefaults(defineProps<Props>(), {
		pageRange: 1
	})
	const emit = defineEmits<{
		(event: 'page-changed', id: number): void
	}>()

	const totalPages = computed(() => props.totalItems)
	const isFirstPage = computed(() => props.currentPage === 1)
	const isLastPage = computed(() => props.currentPage >= totalPages.value)
	const rangeStart = computed(() => {
		const start = Number(props?.currentPage || 0) - Number(props?.pageRange || 0)
		return start > 0 ? start : 1
	})
	const rangeEnd = computed(() => {
		const end = Number(props?.currentPage || 0) + Number(props.pageRange || 0)
		return end < totalPages.value ? end : totalPages.value
	})
	const hasFirst = computed(() => rangeStart.value !== 1)
	const hasLast = computed(() => rangeEnd.value < totalPages.value)
	const changePage = (page: number) => {
		if (page > 0 && page <= totalPages.value) {
			emit('page-changed', page)
		}
	}

	const calculatedPages = computed(() => {
		const pages = []
		for (let i = rangeStart.value; i <= rangeEnd.value; i++) {
			pages.push(i)
		}
		return pages
	})
</script>

<template>
	<nav class="relative z-0 inline-flex rounded-lg" aria-label="Pagination">
		<button
			type="button"
			class="pagination-text pagination-item gap-2 rounded-l-lg bg-white px-4 py-[10px] text-gray-500"
			:class="{
				'cursor-not-allowed': isFirstPage
			}"
			@click="changePage(currentPage - 1)"
		>
			<lazy-tools-icon dir="arrows" icon="arrow-left" stroke="#475467" />
			<span>Prev</span>
		</button>

		<button
			v-if="hasFirst"
			type="button"
			aria-current="page"
			class="pagination-text pagination-item bg-white px-4 py-2 text-gray-500"
			@click.prevent="changePage(1)"
		>
			1
		</button>

		<span v-if="hasFirst" class="pagination-text pagination-item bg-white px-4 py-2 text-gray-700">…</span>

		<button
			v-for="(page, index) in calculatedPages"
			:key="index"
			type="button"
			aria-current="page"
			class="pagination-text pagination-item min-w-[40px] px-2 py-[9px]"
			:class="{
				'bg-gray-50 !text-primary-2': currentPage == page,
				'bg-white text-gray-600': currentPage !== page
			}"
			@click.prevent="changePage(page)"
		>
			{{ page }}
		</button>

		<span v-if="hasLast" class="pagination-text pagination-item bg-white px-4 py-2 text-gray-700">…</span>

		<button
			v-if="hasLast"
			type="button"
			aria-current="page"
			class="pagination-text pagination-item bg-white px-4 py-2 text-gray-500"
			@click.prevent="changePage(totalPages)"
		>
			{{ totalPages }}
		</button>

		<button
			type="button"
			class="pagination-text pagination-item gap-2 rounded-r-lg bg-white px-4 py-[10px]"
			:class="{
				'cursor-not-allowed text-gray-500': isLastPage,
				'text-primary-2': !isLastPage
			}"
			@click.prevent="changePage(currentPage + 1)"
		>
			<span>Next</span>
			<lazy-tools-icon dir="arrows" icon="arrow-right" stroke="#475467" />
		</button>
	</nav>
</template>

<style scoped>
	.pagination-item {
		@apply inline-flex items-center justify-center border border-r-0 border-gray-300 last:border-r;
	}
	.pagination-text {
		@apply text-xs font-medium leading-5;
	}
</style>
